// ── The ADMIN'S delegated Microsoft Graph session — a seam, not an implementation ────────────────
//
// ⚠ WHY THE BROWSER, AND WHY THE ADMIN. VeriGRC's own application token is granted ONE library and cannot
//   discover sites or drives, so it cannot turn a pasted URL into identifiers. The tenant administrator's
//   own signed-in Microsoft session can. Resolution therefore happens here, in the admin's session, and
//   VeriGRC persists only what that session resolved.
//
// ⚠ THIS IS NEVER A VERIGRC USER'S SSO IDENTITY USED FOR THE REPOSITORY. It is a one-off, admin-present
//   lookup during onboarding. Ongoing access is VeriGRC's application identity under the admin's grant.
//
// ⚠ NOT BUILT IN THIS ENVIRONMENT. No Entra application is provisioned for an admin sign-in, so the
//   default provider REFUSES BY NAME. The preview harness and tests inject a fixture client.
//   LIVE-VERIFY: the delegated sign-in and the three Graph reads it makes.

export class DelegatedGraphError extends Error {
  constructor(readonly status: number, readonly code: string | null, message: string) {
    super(message);
    this.name = 'DelegatedGraphError';
  }
}

export class DelegatedSignInUnavailable extends Error {
  constructor() {
    super('Microsoft sign-in for administrators is not set up in this environment, so the library address cannot be looked up yet.');
    this.name = 'DelegatedSignInUnavailable';
  }
}

export interface DelegatedGraphClient {
  /** The Entra directory the admin signed in to — the `tid` of their delegated token. Not a secret. */
  tenantId: string;
  get<T>(path: string): Promise<T>;
}

type Provider = () => Promise<DelegatedGraphClient>;

let provider: Provider = async () => { throw new DelegatedSignInUnavailable(); };

/** The admin's delegated client. Throws DelegatedSignInUnavailable in this build. */
export function getDelegatedGraphClient(): Promise<DelegatedGraphClient> {
  return provider();
}

/** For the preview harness and tests only — swaps in a fixture session. */
export function setDelegatedGraphProvider(next: Provider): void {
  provider = next;
}
